/**
 * Tiny built-in sound effects via the Web Audio API — no asset files. Each cue
 * is a short synthesized envelope. The AudioContext is created/resumed lazily on
 * the first cue (the iframe is opened from a user-initiated flow upstream).
 */
let ctx: AudioContext | null = null;

function ac(): AudioContext | null {
  try {
    if (!ctx) {
      const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      ctx = new Ctor();
    }
    if (ctx.state === 'suspended') void ctx.resume();
    return ctx;
  } catch {
    return null;
  }
}

/** Play a single note with a quick attack/decay envelope. */
function note(freq: number, dur: number, opts: { type?: OscillatorType; gain?: number; delay?: number } = {}): void {
  const c = ac();
  if (!c) return;
  const t0 = c.currentTime + (opts.delay ?? 0);
  const osc = c.createOscillator();
  const g = c.createGain();
  const peak = opts.gain ?? 0.14;
  osc.type = opts.type ?? 'triangle';
  osc.frequency.setValueAtTime(freq, t0);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(peak, t0 + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g).connect(c.destination);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
}

function arpeggio(freqs: number[], step: number, opts: { type?: OscillatorType; gain?: number } = {}): void {
  freqs.forEach((f, i) => note(f, step * 1.6, { ...opts, delay: i * step }));
}

/** Like note() but the frequency ramps from `from` to `to` over the duration. */
function sweep(from: number, to: number, dur: number, opts: { type?: OscillatorType; gain?: number; delay?: number } = {}): void {
  const c = ac();
  if (!c) return;
  const t0 = c.currentTime + (opts.delay ?? 0);
  const osc = c.createOscillator();
  const g = c.createGain();
  const peak = opts.gain ?? 0.1;
  osc.type = opts.type ?? 'sawtooth';
  osc.frequency.setValueAtTime(from, t0);
  osc.frequency.exponentialRampToValueAtTime(to, t0 + dur);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(peak, t0 + dur * 0.4);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g).connect(c.destination);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
}

export const sfx = {
  /** Call once on first user-driven spin to unlock audio. */
  resume(): void {
    ac();
  },
  reelStop(): void {
    note(180, 0.07, { type: 'square', gain: 0.06 });
  },
  win(): void {
    arpeggio([523, 659, 784], 0.07, { type: 'triangle', gain: 0.12 });
  },
  bigWin(): void {
    arpeggio([523, 659, 784, 1047, 1319], 0.08, { type: 'triangle', gain: 0.16 });
  },
  featureStart(): void {
    arpeggio([392, 523, 659, 880], 0.1, { type: 'sawtooth', gain: 0.1 });
  },
  featureComplete(): void {
    arpeggio([784, 659, 523, 659, 880], 0.11, { type: 'triangle', gain: 0.15 });
  },
  /** One tick per payline in the win cycle, rising in pitch. */
  lineTick(i: number): void {
    note(660 * Math.pow(1.06, i), 0.06, { type: 'square', gain: 0.05 });
  },
  /** Rising tick run accompanying a win-amount count-up. */
  rollup(durMs: number): void {
    const steps = Math.max(4, Math.min(24, Math.floor(durMs / 45)));
    for (let i = 0; i < steps; i++) {
      note(880 + (440 * i) / steps, 0.045, { type: 'square', gain: 0.03, delay: (i * durMs) / steps / 1000 });
    }
  },
  expandWhoosh(): void {
    sweep(120, 640, 0.5, { type: 'sawtooth', gain: 0.1 });
  },
  anticipation(): void {
    note(98, 0.3, { type: 'sine', gain: 0.07 });
    note(98, 0.3, { type: 'sine', gain: 0.07, delay: 0.4 });
  },
};
